import { DesignTokens, TailwindColor } from '../types'
import {
  COLOR_VALUES,
  COLOR_VALUES_DARK,
  COLOR_VALUES_LIGHT,
  COLOR_VALUES_BG,
  NEUTRAL_BG,
  NEUTRAL_TEXT,
  NEUTRAL_TEXT_SECONDARY,
  NEUTRAL_BORDER,
} from '../data/tailwindColors'
import {
  getFontByFamily,
  FONT_WEIGHT_VALUES,
  FONT_SIZE_VALUES,
  FONT_SIZE_LINE_HEIGHTS,
  getFontFamilyValue,
  buildGoogleFontsUrl,
} from '../data/googleFonts'

const RADIUS_VALUES = {
  none: '0',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
}

const SHADOW_VALUES = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
}

// Get unique font families that need to be loaded
function getGoogleFontsToLoad(tokens: DesignTokens): string[] {
  const fonts = new Set<string>()
  const families = [
    tokens.typography.displayFont.family,
    tokens.typography.bodyFont.family,
    tokens.typography.monoFont.family,
  ]

  families.forEach((family) => {
    if (family !== 'System UI') {
      const font = getFontByFamily(family)
      if (font && font.googleName) {
        fonts.add(family)
      }
    }
  })

  return Array.from(fonts)
}

export function generateCSSVariables(tokens: DesignTokens, themeName: string): string {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor
  const fontsToLoad = getGoogleFontsToLoad(tokens)

  const lines: string[] = []

  // Add Google Fonts import if needed
  if (fontsToLoad.length > 0) {
    const fontsUrl = buildGoogleFontsUrl(fontsToLoad)
    if (fontsUrl) {
      lines.push(`/* Google Fonts */`)
      lines.push(`@import url('${fontsUrl}');`)
      lines.push(``)
    }
  }

  lines.push(
    `/* ${themeName} - Design System Variables */`,
    `:root {`,
    `  /* Primary Colors */`,
    `  --color-primary: ${COLOR_VALUES[primary]};`,
    `  --color-primary-light: ${COLOR_VALUES_LIGHT[primary]};`,
    `  --color-primary-dark: ${COLOR_VALUES_DARK[primary]};`,
    `  --color-primary-bg: ${COLOR_VALUES_BG[primary]};`,
    ``
  )

  if (tokens.secondaryColor) {
    const secondary = tokens.secondaryColor as TailwindColor
    lines.push(
      `  /* Secondary Colors */`,
      `  --color-secondary: ${COLOR_VALUES[secondary]};`,
      `  --color-secondary-light: ${COLOR_VALUES_LIGHT[secondary]};`,
      `  --color-secondary-dark: ${COLOR_VALUES_DARK[secondary]};`,
      `  --color-secondary-bg: ${COLOR_VALUES_BG[secondary]};`,
      ``
    )
  }

  lines.push(
    `  /* Neutral Colors */`,
    `  --color-neutral-bg: ${NEUTRAL_BG[neutral]};`,
    `  --color-neutral-text: ${NEUTRAL_TEXT[neutral]};`,
    `  --color-neutral-text-secondary: ${NEUTRAL_TEXT_SECONDARY[neutral]};`,
    `  --color-neutral-border: ${NEUTRAL_BORDER[neutral]};`,
    ``,
    `  /* Typography */`,
    `  --font-display: ${getFontFamilyValue(tokens.typography.displayFont.family)};`,
    `  --font-body: ${getFontFamilyValue(tokens.typography.bodyFont.family)};`,
    `  --font-mono: ${getFontFamilyValue(tokens.typography.monoFont.family)};`,
    `  --font-weight-display: ${FONT_WEIGHT_VALUES[tokens.typography.displayFont.weight]};`,
    `  --font-weight-body: ${FONT_WEIGHT_VALUES[tokens.typography.bodyFont.weight]};`,
    `  --font-size-display: ${FONT_SIZE_VALUES[tokens.typography.displayFont.size]};`,
    `  --font-size-body: ${FONT_SIZE_VALUES[tokens.typography.bodyFont.size]};`,
    `  --line-height-display: ${FONT_SIZE_LINE_HEIGHTS[tokens.typography.displayFont.size]};`,
    `  --line-height-body: ${FONT_SIZE_LINE_HEIGHTS[tokens.typography.bodyFont.size]};`,
    ``,
    `  /* Layout */`,
    `  --radius: ${RADIUS_VALUES[tokens.borderRadius]};`,
    `  --shadow: ${SHADOW_VALUES[tokens.shadow]};`,
    `}`
  )

  return lines.join('\n')
}

export function generateTailwindConfig(tokens: DesignTokens, themeName: string): string {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor

  const colorsExtend: string[] = [
    `        primary: colors.${primary},`,
    `        neutral: colors.${neutral},`,
  ]

  if (tokens.secondaryColor) {
    colorsExtend.push(`        secondary: colors.${tokens.secondaryColor},`)
  }

  // Build font family arrays
  const displayFont = tokens.typography.displayFont.family
  const bodyFont = tokens.typography.bodyFont.family
  const monoFont = tokens.typography.monoFont.family

  const displayFontArray = displayFont === 'System UI'
    ? `['system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif']`
    : `['${displayFont}', 'system-ui', 'sans-serif']`

  const bodyFontArray = bodyFont === 'System UI'
    ? `['system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'sans-serif']`
    : `['${bodyFont}', 'system-ui', 'sans-serif']`

  const monoFontArray = `['${monoFont}', 'ui-monospace', 'SFMono-Regular', 'monospace']`

  return `// ${themeName} - Tailwind Configuration
import colors from 'tailwindcss/colors'

export default {
  theme: {
    extend: {
      colors: {
${colorsExtend.join('\n')}
      },
      fontFamily: {
        display: ${displayFontArray},
        body: ${bodyFontArray},
        mono: ${monoFontArray},
      },
      fontWeight: {
        display: '${FONT_WEIGHT_VALUES[tokens.typography.displayFont.weight]}',
        body: '${FONT_WEIGHT_VALUES[tokens.typography.bodyFont.weight]}',
      },
      fontSize: {
        display: ['${FONT_SIZE_VALUES[tokens.typography.displayFont.size]}', { lineHeight: '${FONT_SIZE_LINE_HEIGHTS[tokens.typography.displayFont.size]}' }],
        body: ['${FONT_SIZE_VALUES[tokens.typography.bodyFont.size]}', { lineHeight: '${FONT_SIZE_LINE_HEIGHTS[tokens.typography.bodyFont.size]}' }],
      },
      borderRadius: {
        DEFAULT: '${RADIUS_VALUES[tokens.borderRadius]}',
      },
      boxShadow: {
        DEFAULT: '${SHADOW_VALUES[tokens.shadow]}',
      },
    },
  },
}`
}
